import { useEffect, useRef, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Store, Users, Newspaper, Target, ShieldCheck, UploadCloud, Loader2, Trash2,
  FileText, FileArchive, Film, Image as ImageIcon, Save, Info, Bot, Pencil,
  Power, Zap, Play, Clock, History, CheckCircle2, XCircle, ChevronDown, ChevronRight,
  ClipboardList, Mail, Brain,
} from "lucide-react";
import { TemplateCenter } from "@/components/TemplateCenter";

const CHUNK = 4 * 1024 * 1024; // 4MB

const MODULE_META = {
  rivenditori: {
    icon: Store,
    label: "Rivenditori",
    blurb: "Cerca negozi di bici premium e invia proposte di collaborazione.",
  },
  ambassador: {
    icon: Users,
    label: "Ambassador",
    blurb: "Trova profili social con forte seguito nel mondo e-bike/off-road.",
  },
  redazionali: {
    icon: Newspaper,
    label: "Redazionali",
    blurb: "Invia comunicati stampa a siti e riviste di settore.",
  },
};

function RuleSwitch({ id, label, hint, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-brema-bg px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={id} />
    </div>
  );
}

function MaterialIcon({ mat }) {
  const t = (mat.type || "").toUpperCase();
  if (mat.is_image) return <ImageIcon className="h-5 w-5 text-brand" />;
  if (t === "TESTO" || t === "PDF" || t === "TXT" || t === "DOCX") return <FileText className="h-5 w-5 text-brand" />;
  if (["MP4", "MOV", "WEBM", "VIDEO"].includes(t)) return <Film className="h-5 w-5 text-brand" />;
  return <FileArchive className="h-5 w-5 text-brand" />;
}

function ModuleEditor({ mod, onReload }) {
  const [objective, setObjective] = useState(mod.objective || "");
  const [rules, setRules] = useState(mod.rules || {});
  const [knowledge, setKnowledge] = useState(mod.knowledge || {});
  const [savingCfg, setSavingCfg] = useState(false);

  // material form
  const [title, setTitle] = useState("");
  const [matType, setMatType] = useState((mod.material_types || ["Altro"])[0]);
  const [bodyText, setBodyText] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  // edit material dialog
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", material_type: "", body_text: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (m) => {
    setEditItem(m);
    setEditForm({
      title: m.title || "",
      material_type: m.material_type || (mod.material_types || ["Altro"])[0],
      body_text: m.body_text || "",
    });
  };
  const saveEdit = async () => {
    if (!editForm.title.trim()) { toast.error("Inserisci un titolo"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/agent-modules/${mod.module}/materials/${editItem._id}`, {
        title: editForm.title.trim(), material_type: editForm.material_type, body_text: editForm.body_text,
      });
      toast.success("Materiale aggiornato");
      setEditItem(null);
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Aggiornamento fallito");
    } finally { setSavingEdit(false); }
  };

  useEffect(() => {
    setObjective(mod.objective || "");
    setRules(mod.rules || {});
    setKnowledge(mod.knowledge || {});
  }, [mod._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRule = (k, v) => setRules((r) => ({ ...r, [k]: v }));
  const setK = (k, v) => setKnowledge((s) => ({ ...s, [k]: v }));

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await api.patch(`/agent-modules/${mod.module}`, { objective, rules, knowledge });
      toast.success("Configurazione salvata");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSavingCfg(false); }
  };

  const uploadMaterial = async () => {
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!file && !bodyText.trim()) { toast.error("Aggiungi un file oppure un testo"); return; }
    setUploading(true); setProgress(0);
    try {
      if (file) {
        const { data: initData } = await api.post("/uploads/init");
        const uploadId = initData.upload_id;
        const total = file.size;
        let sent = 0;
        for (let start = 0; start < total; start += CHUNK) {
          const blob = file.slice(start, Math.min(start + CHUNK, total));
          await api.put(`/uploads/${uploadId}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } });
          sent += blob.size;
          setProgress(Math.round((sent / total) * 100));
        }
        await api.post(`/agent-modules/${mod.module}/materials/finalize`, {
          upload_id: uploadId, title: title.trim(), material_type: matType,
          filename: file.name, content_type: file.type || "", body_text: bodyText.trim(),
        });
      } else {
        await api.post(`/agent-modules/${mod.module}/materials/text`, {
          title: title.trim(), material_type: matType, body_text: bodyText.trim(),
        });
      }
      toast.success("Materiale caricato");
      setTitle(""); setBodyText(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito");
    } finally { setUploading(false); setProgress(0); }
  };

  const delMaterial = async (m) => {
    if (!window.confirm(`Eliminare "${m.title}"?`)) return;
    try {
      await api.delete(`/agent-modules/${mod.module}/materials/${m._id}`);
      toast.success("Materiale eliminato");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      {/* Left: objective + rules */}
      <div className="min-w-0 space-y-6 lg:col-span-2">
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-3 flex items-center gap-2 text-foreground">
            <Target className="h-5 w-5 text-brand" /><h3 className="font-semibold">Obiettivo del modulo</h3>
          </div>
          <Textarea
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            data-testid={`module-objective-${mod.module}`}
            className="min-h-[90px] resize-y leading-relaxed"
            placeholder="Descrivi cosa deve fare l'AI Agent per questo gruppo…"
          />
        </div>

        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-1 flex items-center gap-2 text-foreground">
            <ClipboardList className="h-5 w-5 text-brand" /><h3 className="font-semibold">Profilazione (Knowledge Base)</h3>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">Questi dati istruiscono l'AI durante Discovery e Autopilot.</p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Profilo target ideale</Label>
              <Textarea value={knowledge.target_profile || ""} onChange={(e) => setK("target_profile", e.target.value)}
                data-testid={`kb-target-${mod.module}`} rows={2} className="resize-y"
                placeholder="Es. negozi premium multimarca con reparto abbigliamento tecnico…" />
              {mod.module === "ambassador" && (
                <p className="text-[11px] text-muted-foreground" data-testid="kb-target-examples-hint">
                  Inserisci qui anche i link Instagram (es. https://instagram.com/nomeprofilo) dei profili Ambassador considerati in target: vengono usati come <b>esempi positivi</b> (analisi visiva reale dei loro contenuti) per riconoscere il Visual Fit Brema, non come requisiti rigidi.
                  {(() => {
                    const found = [];
                    const re = /(?:instagram\.com|instagr\.am)\/(?:_u\/)?@?([A-Za-z0-9._]{2,30})|(?<![\w.])@([A-Za-z0-9._]{3,30})/gi;
                    let m;
                    while ((m = re.exec(knowledge.target_profile || "")) && found.length < 6) {
                      const h = (m[1] || m[2] || "").replace(/^[/.]+|[/.]+$/g, "").toLowerCase();
                      if (h && !["p", "reel", "reels", "explore", "stories", "accounts", "tv"].includes(h) && !found.includes(h)) found.push(h);
                    }
                    return found.length > 0
                      ? <span className="ml-1 text-positive" data-testid="kb-target-examples-parsed">Esempi positivi riconosciuti: {found.map((h) => `@${h}`).join(", ")}</span>
                      : <span className="ml-1 text-amber-600" data-testid="kb-target-examples-parsed">Nessun link/handle Instagram riconosciuto al momento.</span>;
                  })()}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Regole di esclusione</Label>
              <Textarea value={knowledge.exclusion_rules || ""} onChange={(e) => setK("exclusion_rules", e.target.value)}
                data-testid={`kb-exclusion-${mod.module}`} rows={2} className="resize-y"
                placeholder="Es. escludi officine, noleggio, usato, catene GDO…" />
            </div>
            {mod.module === "rivenditori" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-sm">Condizioni commerciali</Label>
                  <Textarea value={knowledge.commercial_conditions || ""} onChange={(e) => setK("commercial_conditions", e.target.value)}
                    data-testid={`kb-conditions-${mod.module}`} rows={2} className="resize-y"
                    placeholder="Es. ordine minimo, tempi di consegna, resi…" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Margini</Label>
                  <Textarea value={knowledge.margins || ""} onChange={(e) => setK("margins", e.target.value)}
                    data-testid={`kb-margins-${mod.module}`} rows={2} className="resize-y"
                    placeholder="Es. margine rivenditore, sconti a scaglioni (uso interno)…" />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-3 flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-5 w-5 text-brand" /><h3 className="font-semibold">Regole</h3>
          </div>
          <div className="space-y-3">
            {mod.module === "rivenditori" && (
              <>
                <RuleSwitch id={`rule-alert_on_pricing_interest-${mod.module}`}
                  label="Avvisami se un rivenditore chiede il listino"
                  hint="L'AI segnala subito i lead interessati ai prezzi."
                  checked={rules.alert_on_pricing_interest}
                  onChange={(v) => setRule("alert_on_pricing_interest", v)} />
                <RuleSwitch id={`rule-alert_inapp-${mod.module}`}
                  label="Notifica dentro l'app"
                  checked={rules.alert_inapp}
                  onChange={(v) => setRule("alert_inapp", v)} />
                <RuleSwitch id={`rule-alert_email-${mod.module}`}
                  label="Notifica via email"
                  checked={rules.alert_email}
                  onChange={(v) => setRule("alert_email", v)} />
              </>
            )}
            {mod.module === "ambassador" && (
              <>
                <div className="rounded-lg border border-border bg-brema-bg px-4 py-3">
                  <Label className="text-sm font-medium text-foreground">Fascia follower</Label>
                  <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Cerca solo profili con un numero di follower compreso in questa fascia. Vale per Autopilot e per la ricerca manuale.</p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Minimo</Label>
                      <Input type="number" min={0} step={500}
                        value={rules.min_followers ?? 1500}
                        onChange={(e) => setRule("min_followers", Number(e.target.value) || 0)}
                        data-testid={`rule-min-followers-${mod.module}`} className="h-9 max-w-[160px]" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Massimo</Label>
                      <Input type="number" min={0} step={500}
                        value={rules.max_followers ?? 300000}
                        onChange={(e) => setRule("max_followers", Number(e.target.value) || 0)}
                        data-testid={`rule-max-followers-${mod.module}`} className="h-9 max-w-[160px]" />
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-brema-bg px-4 py-3">
                  <Label className="text-sm font-medium text-foreground">Soglia di qualificazione Ambassador</Label>
                  <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Punteggio minimo (0-100) della pipeline unica (Knowledge Base + dati reali + analisi visiva) per considerare un profilo <b>Qualificato</b>. Sotto soglia → "Da verificare". Vale per ricerca manuale, AI Discovery, Autopilot e Comando Rapido.</p>
                  <Input type="number" min={40} max={100} step={5}
                    value={rules.qualify_threshold ?? 70}
                    onChange={(e) => setRule("qualify_threshold", Math.max(40, Math.min(100, Number(e.target.value) || 70)))}
                    data-testid={`rule-qualify-threshold-${mod.module}`} className="h-9 max-w-[160px]" />
                </div>
                <RuleSwitch id={`rule-require_high_quality-${mod.module}`}
                  label="Richiedi stile fotografico di alta qualità"
                  hint="Privilegia profili con feed curati e foto professionali."
                  checked={rules.require_high_quality}
                  onChange={(v) => setRule("require_high_quality", v)} />
              </>
            )}
            {mod.module === "redazionali" && (
              <>
                <RuleSwitch id={`rule-find_editor_contact-${mod.module}`}
                  label="Trova il contatto della redazione / editor"
                  hint="L'AI cerca l'email della redazione prima di inviare."
                  checked={rules.find_editor_contact}
                  onChange={(v) => setRule("find_editor_contact", v)} />
                <RuleSwitch id={`rule-attach_materials-${mod.module}`}
                  label="Allega automaticamente i materiali"
                  hint="Comunicato stampa e foto collezione allegati al pitch."
                  checked={rules.attach_materials}
                  onChange={(v) => setRule("attach_materials", v)} />
              </>
            )}
            <div className="rounded-lg border border-border bg-brema-bg px-4 py-3">
              <Label className="text-sm font-medium text-foreground">Regole aggiuntive per l'AI (testo libero)</Label>
              <Textarea value={rules.custom_rules || ""} onChange={(e) => setRule("custom_rules", e.target.value)}
                data-testid={`rule-custom-${mod.module}`} rows={3} className="mt-2 resize-y"
                placeholder="Es. tono formale, evita concorrenti diretti, cita la collezione SS26…" />
            </div>
          </div>
          <Button onClick={saveConfig} disabled={savingCfg} data-testid={`module-save-btn-${mod.module}`}
            className="mt-4 w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {savingCfg ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva configurazione</>}
          </Button>
        </div>
      </div>

      {/* Right: materials */}
      <div className="min-w-0 space-y-6 lg:col-span-3">
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-4 flex items-center gap-2 text-foreground">
            <UploadCloud className="h-5 w-5 text-brand" /><h3 className="font-semibold">File aziendali di supporto</h3>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">Documenti di riferimento (catalogo, listino, FAQ, note) che aiutano l'AI a profilare e qualificare i contatti in Discovery/Autopilot. Per i modelli d'invio con allegati usa il <b>Template Center</b>.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Titolo</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`material-title-${mod.module}`}
                placeholder="Es. Catalogo Brema Bike 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={matType} onValueChange={setMatType}>
                <SelectTrigger data-testid={`material-type-${mod.module}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(mod.material_types || ["Altro"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            <Label>Testo / Script (opzionale)</Label>
            <Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid={`material-text-${mod.module}`}
              rows={3} placeholder="Incolla qui lo script email/DM oppure lascia vuoto se carichi un file." />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`material-file-${mod.module}`}
              className="text-sm text-foreground file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-foreground" />
            <Button onClick={uploadMaterial} disabled={uploading} data-testid={`material-upload-btn-${mod.module}`}
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {progress}%</> : <><UploadCloud className="mr-2 h-4 w-4" /> Carica materiale</>}
            </Button>
          </div>
          {uploading && file && (
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-brema-bg" data-testid="material-upload-progress">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Puoi caricare un file (PDF, foto, ZIP) e/o incollare una nota di testo. Questi file restano come riferimento interno per l'AI.</p>
        </div>

        <div className="rounded-xl border border-border bg-brema-card">
          <div className="border-b border-border p-4">
            <h3 className="font-semibold text-foreground">File di supporto ({mod.materials?.length || 0})</h3>
          </div>
          {(!mod.materials || mod.materials.length === 0) ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nessun file di supporto. Carica il primo documento di riferimento qui sopra.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {mod.materials.map((m) => (
                <div key={m._id} data-testid="module-material-card" className="flex items-start gap-3 p-4">
                  <div className="mt-0.5 shrink-0"><MaterialIcon mat={m} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">{m.title}</p>
                      <span className="rounded-full bg-brema-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">{m.material_type}</span>
                      <span className="text-[11px] text-muted-foreground">{m.type} · {m.size}</span>
                    </div>
                    {m.body_text && <p className="mt-1.5 line-clamp-2 whitespace-pre-line rounded-md bg-brema-bg p-2 text-xs text-foreground">{m.body_text}</p>}
                    {m.file_path && (
                      <a href={fileUrl(m.file_path)} target="_blank" rel="noreferrer"
                        data-testid="module-material-download" className="mt-1.5 inline-block text-xs font-medium text-brand hover:underline">
                        Apri / scarica file
                      </a>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button size="sm" variant="secondary" data-testid="module-material-edit-btn" onClick={() => openEdit(m)}
                      className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-bg">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="danger" data-testid="module-material-delete-btn" onClick={() => delMaterial(m)}
                      className="rounded-full text-xs">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="material-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica materiale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Titolo</Label>
              <Input value={editForm.title} data-testid="material-edit-title"
                onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={editForm.material_type} onValueChange={(v) => setEditForm((p) => ({ ...p, material_type: v }))}>
                <SelectTrigger data-testid="material-edit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(mod.material_types || ["Altro"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{editItem?.type === "TESTO" ? "Script / testo" : "Descrizione / note"}</Label>
              <Textarea rows={5} value={editForm.body_text} data-testid="material-edit-text"
                placeholder={editItem?.type === "TESTO" ? "Testo dello script…" : "Descrizione o note associate al file…"}
                onChange={(e) => setEditForm((p) => ({ ...p, body_text: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditItem(null)} data-testid="material-edit-cancel">Annulla</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="material-edit-save">
              {savingEdit ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva modifiche</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AP_INTERVALS = [
  { v: 0, label: "Solo manuale" },
  { v: 6, label: "Ogni 6 ore" },
  { v: 12, label: "Ogni 12 ore" },
  { v: 24, label: "Ogni 24 ore" },
];
const AP_MEDIA_CATS = ["Bike Tecnico", "Gravel/Outdoor", "Lifestyle & Moda"];

function AutopilotConfig({ module, cfg, masterEnabled, onReload }) {
  const [enabled, setEnabled] = useState(!!cfg?.enabled);
  const [intervalH, setIntervalH] = useState(String(cfg?.interval_hours ?? 0));
  const [params, setParams] = useState(cfg?.params || {});
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  useEffect(() => {
    setEnabled(!!cfg?.enabled);
    setIntervalH(String(cfg?.interval_hours ?? 0));
    setParams(cfg?.params || {});
  }, [cfg?.module]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg) return null;
  const setP = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  const save = async (nextEnabled) => {
    setSaving(true);
    try {
      const body = {
        enabled: nextEnabled !== undefined ? nextEnabled : enabled,
        interval_hours: Number(intervalH) || 0,
        params: { ...params, count: Number(params.count) || 0 },
      };
      const { data } = await api.patch(`/autopilot/${module}`, body);
      setParams(data.autopilot.params);
      toast.success("Impostazioni Autopilot salvate");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  const toggleEnabled = (v) => { setEnabled(v); save(v); };

  const runNow = async () => {
    setRunningNow(true);
    try {
      await api.post(`/autopilot/${module}/run`);
      toast.success("Autopilot avviato · la proposta apparirà nella Coda di approvazione");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Avvio fallito");
    } finally { setRunningNow(false); }
  };

  const lastRun = cfg.last_run_at
    ? new Date(cfg.last_run_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div data-testid={`autopilot-config-${module}`} className={`mb-6 rounded-xl border p-5 transition-colors ${enabled ? "border-brand/50 bg-brand/5" : "border-border bg-brema-card"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-foreground">
          <Zap className={`h-5 w-5 ${enabled ? "text-brand" : "text-muted-foreground"}`} />
          <h3 className="font-semibold">Autopilot — {MODULE_META[module]?.label || module}</h3>
          {!masterEnabled && enabled && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300" data-testid={`autopilot-config-paused-${module}`}>Master in pausa</span>
          )}
        </div>
        <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid={`autopilot-config-switch-${module}`} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Quando attivo, l'AI cerca autonomamente contatti (usando obiettivo, regole e materiali qui sotto) e prepara le bozze nella Coda di approvazione. Nessun invio automatico.</p>

      {enabled && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs"><Clock className="h-3.5 w-3.5" /> Frequenza</Label>
              <Select value={intervalH} onValueChange={setIntervalH}>
                <SelectTrigger data-testid={`autopilot-interval-${module}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AP_INTERVALS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">N° contatti per ciclo</Label>
              <Input type="number" min={1} max={30} value={params.count ?? ""} onChange={(e) => setP("count", e.target.value)}
                data-testid={`autopilot-count-${module}`} className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nazione</Label>
              <Input value={params.nation ?? ""} onChange={(e) => setP("nation", e.target.value)}
                data-testid={`autopilot-nation-${module}`} className="h-9" placeholder="Italia" />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {(module === "rivenditori" || module === "ambassador") && (
              <div className="space-y-1.5">
                <Label className="text-xs">Regione (opz.)</Label>
                <Input value={params.region ?? ""} onChange={(e) => setP("region", e.target.value)}
                  data-testid={`autopilot-region-${module}`} className="h-9" placeholder="Es. Lombardia" />
              </div>
            )}
            {module === "rivenditori" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Canale</Label>
                <Select value={params.channel || "email"} onValueChange={(v) => setP("channel", v)}>
                  <SelectTrigger data-testid={`autopilot-channel-${module}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {module === "ambassador" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Disciplina</Label>
                <Input value={params.discipline ?? ""} onChange={(e) => setP("discipline", e.target.value)}
                  data-testid={`autopilot-discipline-${module}`} className="h-9" placeholder="Es. e-bike, gravel" />
              </div>
            )}
            {module === "redazionali" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Categoria media</Label>
                <Select value={params.media_category || "Bike Tecnico"} onValueChange={(v) => setP("media_category", v)}>
                  <SelectTrigger data-testid={`autopilot-media-cat-${module}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AP_MEDIA_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => save()} disabled={saving} data-testid={`autopilot-save-${module}`}
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva impostazioni</>}
            </Button>
            <Button onClick={runNow} disabled={runningNow || !masterEnabled} variant="secondary" data-testid={`autopilot-run-now-${module}`}
              className="rounded-full border border-brand/40 bg-transparent font-semibold text-brand hover:bg-brand/10 disabled:opacity-50">
              {runningNow ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Avvio…</> : <><Play className="mr-2 h-4 w-4" /> Esegui Autopilot ora</>}
            </Button>
            {!masterEnabled && (
              <span className="text-xs text-amber-700 dark:text-amber-300" data-testid={`autopilot-run-now-blocked-${module}`}>
                Attiva l'interruttore master per eseguire l'Autopilot.
              </span>
            )}
            {lastRun && <span className="text-xs text-muted-foreground" data-testid={`autopilot-last-run-${module}`}>Ultima esecuzione: {lastRun}</span>}
          </div>
        </div>
      )}

      {!enabled && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button disabled variant="secondary" data-testid={`autopilot-run-now-${module}`}
            className="rounded-full border border-brand/40 bg-transparent font-semibold text-brand disabled:opacity-50">
            <Play className="mr-2 h-4 w-4" /> Esegui Autopilot ora
          </Button>
          <span className="text-xs text-amber-700 dark:text-amber-300" data-testid={`autopilot-run-now-blocked-${module}`}>
            {!masterEnabled ? "Attiva l'interruttore master e questo modulo per eseguire l'Autopilot." : "Attiva l'Autopilot per questo modulo per eseguirlo."}
          </span>
        </div>
      )}
    </div>
  );
}

const AP_MODULE_LABEL = { rivenditori: "Rivenditori", ambassador: "Ambassador", redazionali: "Redazionali" };

function AutopilotHistory() {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    api.get("/autopilot/history?limit=50")
      .then(({ data }) => setJobs(data.jobs || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  };
  useEffect(() => { if (open && !loaded) load(); /* eslint-disable-next-line */ }, [open]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="mt-8 rounded-2xl border border-border bg-brema-card p-5" data-testid="autopilot-history">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="autopilot-history-toggle"
        className="flex w-full items-center justify-between gap-2 text-left">
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <History className="h-5 w-5 text-brand" /> Registro Autopilot
          <span className="text-xs font-normal text-muted-foreground">proposte inviate</span>
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="mt-4">
          {!loaded ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
          ) : jobs.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="autopilot-history-empty">Nessuna proposta ancora archiviata. Le proposte appariranno qui dopo l'invio o lo scarto.</p>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => {
                const sent = j.outcome === "sent";
                return (
                  <div key={j.id} data-testid="autopilot-history-row" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-brema-bg p-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                        {sent
                          ? <span className="inline-flex items-center gap-1 rounded-full bg-positive/10 px-2 py-0.5 text-[11px] text-positive"><CheckCircle2 className="h-3 w-3" /> Inviata</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-brema-card px-2 py-0.5 text-[11px] text-muted-foreground"><XCircle className="h-3 w-3" /> Scartata</span>}
                        {AP_MODULE_LABEL[j.module] || j.module}
                        {sent && <span className="text-xs text-muted-foreground">· {j.sent} contatti</span>}
                      </p>
                      {j.summary && <p className="mt-0.5 truncate text-xs text-muted-foreground">{j.summary}</p>}
                    </div>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">{fmt(j.resolved_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentModules() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autopilot, setAutopilot] = useState({ master_enabled: false, modules: [] });
  const [savingMaster, setSavingMaster] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [topTab, setTopTab] = useState("knowledge");

  const load = () => {
    if (!modules.length) setLoading(true);
    api.get("/agent-modules")
      .then(({ data }) => {
        setModules(data.modules || []);
        setActiveTab((prev) => prev || data.modules?.[0]?.module || "rivenditori");
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Caricamento fallito"))
      .finally(() => setLoading(false));
  };
  const loadAutopilot = () => {
    api.get("/autopilot")
      .then(({ data }) => setAutopilot(data))
      .catch(() => {});
  };
  useEffect(() => { load(); loadAutopilot(); }, []);

  const toggleMaster = async (v) => {
    setSavingMaster(true);
    try {
      await api.patch("/autopilot/master", { enabled: v });
      setAutopilot((a) => ({ ...a, master_enabled: v }));
      toast.success(v ? "Autopilot AI Agent attivato" : "Autopilot AI Agent in pausa");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSavingMaster(false); }
  };

  const apFor = (mod) => autopilot.modules.find((m) => m.module === mod);

  const order = ["rivenditori", "ambassador", "redazionali"];
  const sorted = [...modules].sort((a, b) => order.indexOf(a.module) - order.indexOf(b.module));

  return (
    <div data-testid="agent-modules-page">
      <PageHeader
        eyebrow="Configurazione AI Agent"
        title="Moduli AI"
        subtitle="Due aree separate: la Knowledge Base che istruisce l'AI in Discovery/Autopilot e il Template Center per gli invii."
      />

      <Tabs value={topTab} onValueChange={setTopTab} data-testid="agent-modules-toptabs">
        <TabsList className="mb-6 bg-brema-card">
          <TabsTrigger value="knowledge" data-testid="toptab-knowledge">
            <Brain className="mr-1.5 h-4 w-4" /> Discovery Knowledge Base
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="toptab-templates">
            <Mail className="mr-1.5 h-4 w-4" /> Template Center
          </TabsTrigger>
        </TabsList>

        <TabsContent value="knowledge">
      {/* Master Autopilot toggle */}
      <div data-testid="autopilot-master" className={`mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5 transition-colors ${autopilot.master_enabled ? "border-brand/50 bg-brand/10" : "border-border bg-brema-card"}`}>
        <div className="flex items-start gap-3">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${autopilot.master_enabled ? "bg-brand text-white" : "bg-brema-bg text-muted-foreground"}`}>
            <Power className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground">Attiva AI Agent Autopilot
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${autopilot.master_enabled ? "bg-positive/15 text-positive" : "bg-brema-bg text-muted-foreground"}`}>{autopilot.master_enabled ? "Attivo" : "In pausa"}</span>
            </h3>
            <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">Interruttore generale dell'automazione. Quando è attivo, i moduli con Autopilot acceso e un intervallo impostato preparano da soli le proposte nella <b>Coda di approvazione</b>. Le email partono solo dopo la tua approvazione.</p>
          </div>
        </div>
        <Switch checked={autopilot.master_enabled} disabled={savingMaster} onCheckedChange={toggleMaster} data-testid="autopilot-master-switch" />
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/10 p-4" data-testid="modules-info-banner">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
        <p className="text-sm text-foreground">
          Imposta <b>obiettivo</b>, <b>profilazione</b> (target, esclusioni, condizioni, margini) e <b>regole</b> di ogni gruppo, e carica i <b>file di supporto</b>. Questi dati guidano l'AI in <b>Discovery</b> e <b>Autopilot</b>. I modelli d'invio con testo e allegati si gestiscono nel <b>Template Center</b>.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="modules-loading">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Caricamento moduli…
        </div>
      ) : (
        <Tabs value={activeTab || sorted[0]?.module || "rivenditori"} onValueChange={setActiveTab} data-testid="modules-tabs">
          <TabsList className="flex-wrap bg-brema-card">
            {sorted.map((m) => {
              const meta = MODULE_META[m.module] || { icon: Bot, label: m.title };
              const Icon = meta.icon;
              const ap = apFor(m.module);
              return (
                <TabsTrigger key={m.module} value={m.module} data-testid={`module-tab-${m.module}`}>
                  <Icon className="mr-1.5 h-4 w-4" /> {meta.label}
                  {ap?.enabled && <Zap className="ml-1.5 h-3 w-3 text-brand" data-testid={`module-tab-ap-${m.module}`} />}
                  <span className="ml-2 rounded-full bg-brema-bg px-1.5 text-[10px] text-muted-foreground">{m.materials?.length || 0}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          {sorted.map((m) => (
            <TabsContent key={m.module} value={m.module} className="mt-6">
              <p className="mb-5 text-sm text-muted-foreground">{MODULE_META[m.module]?.blurb}</p>
              <AutopilotConfig module={m.module} cfg={apFor(m.module)} masterEnabled={autopilot.master_enabled} onReload={loadAutopilot} />
              <ModuleEditor mod={m} onReload={load} />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <AutopilotHistory />
        </TabsContent>

        <TabsContent value="templates" className="mt-2">
          <TemplateCenter />
        </TabsContent>
      </Tabs>
    </div>
  );
}
